import { MONTHS } from 'app-constants';
import { getDocs, Timestamp } from 'firebase/firestore';
import React from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { FilterItem, OrderItem, getArtistRevenueQuery, getPuzzlesByUser } from 'services';
import { Splash } from 'shared/Components';
import FilterControle from 'shared/Components/FlexebleTable/Components/FilterControle';
import FlexebleTable from 'shared/Components/FlexebleTable/FlexebleTable';
import { Option } from 'shared/Components/Select/Select';
import { Text } from 'shared/Typhography';
import styled from 'styled-components';

const Icon = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 15px;
  margin-right: 10px;
`;

interface TotalRevenueSchema {
  dateInterval: {
    start?: Timestamp;
    end?: Timestamp;
  };
  artistRevenue: number;
  total: number;
}

export const RevenueTable = () => {
  const { artistId } = useParams<{ artistId: string }>();
  const { data: artistPuzzlesRaw, isLoading: isLoadingArtistPuzzles } = getPuzzlesByUser(artistId);

  const [data, setData] = React.useState<any | undefined>();
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [filters, setFilters] = React.useState<{
    firebase: FilterItem[];
    fields: { [key: string]: any };
  }>({ firebase: [], fields: {} });
  const [ordering, setOrdering] = React.useState<OrderItem[]>([]);
  const [artistPuzzles, setArtistPuzzles] = React.useState<Option[]>([]);

  const [totalRevenue, setTotalRevenue] = React.useState<TotalRevenueSchema | undefined>();
  const [isLoadingTotalRevenue, setLoadingTotalRevenue] = React.useState<boolean>(true);
  const fetchTotalRevenue = async () => {
    setLoadingTotalRevenue(true);
    const startDate = filters.firebase.find(
      (filter) => filter.attribute === 'createdAt' && filter.operator === '>=',
    )?.value;
    const endDate = filters.firebase.find(
      (filter) => filter.attribute === 'createdAt' && filter.operator === '<=',
    )?.value;

    try {
      const totalRevenueQuery = getArtistRevenueQuery(
        artistId,
        filters.firebase,
        ordering,
        undefined,
        undefined,
      );

      const totalRevenueDocs = await getDocs(totalRevenueQuery);
      setTotalRevenue({
        dateInterval: {
          start: startDate ? (startDate as Timestamp) : undefined,
          end: endDate ? (endDate as Timestamp) : undefined,
        },
        artistRevenue: totalRevenueDocs.docs.reduce(
          (accumulation, next) => accumulation + next.data().revenue * (next.data().shares ?? 1),
          0,
        ),
        total: totalRevenueDocs.docs.reduce(
          (accumulation, next) => accumulation + next.data().revenue,
          0,
        ),
      });
    } catch (err) {
      toast.error('Faild to fetch total revenue');
      console.log(err);
    }
    setLoadingTotalRevenue(false);
  };

  const fetch = async (reset = false) => {
    setLoading(true);
    try {
      const nextQuery = getArtistRevenueQuery(
        artistId,
        filters.firebase,
        ordering,
        data?.length > 0 && !reset ? data[data?.length - 1] : undefined,
        20,
      );

      const nextData = await getDocs(nextQuery);
      setData(
        !reset
          ? [...data, ...nextData.docs.filter((doc) => doc.exists())]
          : nextData.docs.filter((doc) => doc.exists()),
      );
    } catch (err) {
      console.log(err);
      toast.error(`${err}`);
    }
    setLoading(false);
  };

  const manageOrdering = (attribute: string) => {
    const orderingItem = ordering.find((item) => item.field === attribute);

    if (orderingItem) {
      if (orderingItem.type === 'asc')
        setOrdering(
          ordering.map((item) => (item?.field === attribute ? { ...item, type: 'desc' } : item)),
        );
      else setOrdering(ordering.filter((item) => item?.field !== attribute));

      return;
    }

    setOrdering([...ordering, { field: attribute, type: 'asc' }]);
  };

  React.useEffect(() => {
    if (!isLoadingArtistPuzzles)
      setArtistPuzzles(
        artistPuzzlesRaw?.docs
          ? artistPuzzlesRaw?.docs.map((item) => {
              return { value: item?.id, label: item?.data()?.Name ?? item?.data()?.name };
            })
          : [],
      );
  }, [isLoadingArtistPuzzles]);

  React.useEffect(() => {
    if (!isLoading) fetch(true);
  }, [filters.firebase, ordering]);

  React.useEffect(() => {
    fetchTotalRevenue();
  }, [filters.firebase]);

  return (
    <>
      {data && (
        <>
          <FilterControle
            loading={isLoading}
            onChange={(item) => {
              setFilters({
                firebase: [
                  ...filters.firebase.filter((filter) => filter?.attribute !== item?.name),
                  { attribute: item?.name, value: item?.value, operator: '==' },
                ],
                fields: {
                  ...filters.fields,
                  [item.name]: item,
                },
              });
            }}
            clear={(name: string) => {
              setFilters({
                firebase: filters.firebase.filter((item: any) => item?.attribute !== name),
                fields: {
                  ...filters.fields,
                  [name]: undefined,
                },
              });
            }}
            values={filters.fields}
            keys={[
              {
                name: 'date',
                placeholder: 'Date Range',
                options: [],
                type: 'date-range',
                disabled: isLoading,
                onChange: (dates: [Date | null, Date | null]) => {
                  const start = dates['0'];
                  const end = dates['1'];
                  if (end) {
                    end.setHours(23);
                    end.setMinutes(59);
                    end.setSeconds(59);
                  }

                  if (!start || !end) {
                    if (filters.firebase.find((fil) => fil.attribute === 'date')) {
                      setFilters({
                        firebase: filters.firebase.filter((fil) => fil.attribute !== 'date'),
                        fields: {
                          ...filters.fields,
                          createdAt: undefined,
                        },
                      });
                    }
                    return;
                  }

                  let firebaseFilter: FilterItem[] = filters.firebase;
                  if (start && end) {
                    firebaseFilter.push({
                      value: Timestamp.fromDate(start),
                      attribute: 'date',
                      operator: '>=',
                    });

                    firebaseFilter.push({
                      value: Timestamp.fromDate(end),
                      attribute: 'date',
                      operator: '<=',
                    });
                  } else {
                    firebaseFilter = firebaseFilter.filter((item) => item?.attribute !== 'date');
                  }

                  setFilters({
                    firebase: [...firebaseFilter],
                    fields: {
                      ...filters.fields,
                      date: { startDate: start, endDate: end },
                    },
                  });
                },
              },
              {
                name: 'puzzleId',
                placeholder: 'Select Puzzle',
                options: artistPuzzles,
                disabled: isLoadingArtistPuzzles,
              },
            ]}
          />

          <Text margin="20px 0" size={2} weight="bold">
            Total Revenue
          </Text>
          {!isLoadingTotalRevenue && (
            <FlexebleTable
              data={[totalRevenue]}
              schema={[
                {
                  header: 'Date Interval',
                  key: 'date',
                  mapper: (item: TotalRevenueSchema) => {
                    const parseDate = (date: Date) =>
                      `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;

                    const startDate = item.dateInterval.start
                      ? item.dateInterval.start.toDate()
                      : 'Not Set';
                    const endDate = item.dateInterval.end
                      ? item.dateInterval.end.toDate()
                      : 'Not Set';

                    return `${typeof startDate === 'string' ? startDate : parseDate(startDate)} - ${
                      typeof endDate === 'string' ? endDate : parseDate(endDate)
                    }`;
                  },
                },
                {
                  header: 'Artist Royalties',
                  key: 'artistRevenue',
                  mapper: (item) => `$${item.artistRevenue.toFixed(5)}`,
                },
                {
                  header: 'Total Revenue',
                  key: 'total',
                  mapper: (item) => `$${item.total.toFixed(5)}`,
                },
              ]}
            />
          )}
          {isLoadingTotalRevenue && <Splash position="relative" width={64} />}

          <Text margin="20px 0" size={2} weight="bold">
            Revenue Insights
          </Text>
          <FlexebleTable
            onScroll={() => {
              if (!isLoading) fetch();
            }}
            scrollThreshold={30}
            ordering={ordering}
            data={data}
            schema={[
              {
                header: 'Puzzle',
                key: 'puzzleId',
                mapper: (item) => {
                  const puzzle = artistPuzzlesRaw?.docs?.find(
                    (pzitem) => pzitem.id === item?.data()?.puzzleId,
                  );

                  return (
                    <>
                      <Icon src={puzzle?.data()?.iconURL} />{' '}
                      {puzzle?.data()?.name ?? puzzle?.data()?.Name}
                    </>
                  );
                },
                headerClick: () => manageOrdering('puzzleId'),
                style: {
                  cell: {
                    height: '80px',
                  },
                },
              },
              {
                header: 'Created At',
                key: 'date',
                mapper: (item) => {
                  const date = item?.data()?.date?.toDate();
                  if (!date) return 'Not Set';

                  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
                },
                headerClick: () => manageOrdering('date'),
                style: {
                  cell: {
                    height: '80px',
                  },
                },
              },
              {
                header: 'Artist Royalties',
                key: 'revenue',
                mapper: (item) =>
                  `$${(item.data().revenue * (item.data().shares ?? 1)).toFixed(5)}`,
                headerClick: () => manageOrdering('revenue'),
                style: {
                  cell: {
                    height: '80px',
                  },
                },
              },
              {
                header: 'Revenue',
                key: 'totalRevenue',
                mapper: (item) => `$${item.data().revenue.toFixed(5)}`,
                headerClick: () => manageOrdering('totalRevenue'),
                style: {
                  cell: {
                    height: '80px',
                  },
                },
              },
            ]}
          />
        </>
      )}
      {isLoading && <Splash position="relative" width={64} />}
    </>
  );
};
